import React, { useState, useEffect, useCallback } from 'react';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { useGoogleMaps } from '../../../../../hooks/useGoogleMaps.ts';
import styles from './PlacesAutocompleteModal.module.scss';

interface PlacesAutocompleteModalProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (place: {
    address: string;
    lat: number;
    lng: number;
  }) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const PlacesAutocompleteModalInner: React.FC<PlacesAutocompleteModalProps> = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Pilar, Buenos Aires",
  className = "",
  disabled = false
}) => {
  const {
    ready,
    value: inputValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'ar' },
      types: ['establishment', 'geocode'],
    },
    debounce: 300,
  });

  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (value !== inputValue) {
      setValue(value, false);
    }
  }, [value, inputValue, setValue]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    onChange(newValue);
  }, [setValue, onChange]);

  const handleSelect = useCallback(async (suggestion: any) => {
    const { place_id, description } = suggestion;

    try {
      const results = await getGeocode({ placeId: place_id });
      const { lat, lng } = await getLatLng(results[0]);

      setValue(description, false);
      onChange(description);
      clearSuggestions();
      setIsFocused(false);

      if (onPlaceSelect) {
        onPlaceSelect({
          address: description,
          lat,
          lng
        });
      }
    } catch (error) {
      console.error('Error al obtener coordenadas:', error);
      setValue(description, false);
      onChange(description);
      clearSuggestions();
      setIsFocused(false);
    }
  }, [setValue, onChange, clearSuggestions, onPlaceSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      clearSuggestions();
      setIsFocused(false);
    }
  }, [clearSuggestions]);

  const hasSuggestions = status === 'OK' && data.length > 0;
  const isLoading = status === 'LOADING';
  const noResults = status === 'ZERO_RESULTS' && inputValue;

  return (
    <div className={`${styles.container} ${className}`}>
      <input
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setTimeout(() => {
            clearSuggestions();
            setIsFocused(false);
          }, 200);
        }}
        disabled={disabled || !ready}
        placeholder={placeholder}
        className={styles.input}
        autoComplete="off"
      />

      {(isFocused || hasSuggestions || isLoading || noResults) && (
        <div className={styles.dropdown}>
          {isLoading && (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <span>Buscando...</span>
            </div>
          )}

          {hasSuggestions && data.map((suggestion) => {
            const {
              place_id,
              structured_formatting: { main_text, secondary_text },
            } = suggestion;

            return (
              <div
                key={place_id}
                className={styles.suggestionItem}
                onClick={() => handleSelect(suggestion)}
                onMouseDown={(e) => e.preventDefault()}
              >
                <div className={styles.suggestionContent}>
                  <div className={styles.mainText}>{main_text}</div>
                  {secondary_text && (
                    <div className={styles.secondaryText}>{secondary_text}</div>
                  )}
                </div>
              </div>
            );
          })}

          {noResults && (
            <div className={styles.noResults}>
              No se encontraron ubicaciones para "{inputValue}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PlacesAutocompleteModal: React.FC<PlacesAutocompleteModalProps> = (props) => {
  const { isLoaded } = useGoogleMaps();

  if (!isLoaded) {
    return (
      <div className={styles.container}>
        <input
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          placeholder={props.placeholder}
          className={styles.input}
          disabled={props.disabled}
        />
      </div>
    );
  }

  return <PlacesAutocompleteModalInner {...props} />;
};

export default PlacesAutocompleteModal;
